import * as React from 'react';
import { Query } from 'react-apollo';
import { sortBy } from 'lodash';

import AddDeviceMenuButton from 'settings/components/AddDeviceMenuButton';
import Container from 'components/Container';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import DeviceSettingsDesktopItem from 'settings/components/DeviceSettingsItems/DeviceSettingsDesktopItem';
import DeviceSettingsEmailItem from 'settings/components/DeviceSettingsItems/DeviceSettingsEmailItem';
import DeviceSettingsEmptyItem from 'settings/components/DeviceSettingsItems/DeviceSettingsEmptyItem';
import DeviceSettingsLandlineItem from 'settings/components/DeviceSettingsItems/DeviceSettingsLandlineItem';
import DeviceSettingsPendingSMSItem from 'settings/components/DeviceSettingsItems/DeviceSettingsPendingSMSItem';
import DeviceSettingsPushItem from 'settings/components/DeviceSettingsItems/DeviceSettingsPushItem';
import DeviceSettingsSMSItem from 'settings/components/DeviceSettingsItems/DeviceSettingsSMSItem';
import DEVICES_QUERY from 'settings/queries/devicesQuery.gql';
import IS_ROSTERED_ENFORCEMENT from 'settings/queries/IsRosteredEnforcement.gql';
import ErrorReporter from 'app/components/ErrorReporter';
import Legend from 'components/Legend';
import Row from 'components/Row';

import {
  Devices,
  Devices_devices,
  Devices_me_pendingSmsDevices,
} from 'settings/queries/__generated__/Devices';
import { IsRosteredEnforcement } from 'settings/queries/__generated__/IsRosteredEnforcement';

export type DeviceSettingsDevice = Devices_devices;
export type DeviceSettingsPendingSMSDevice = Devices_me_pendingSmsDevices;

export type Props = {
  showSmsDevices: boolean;
};

export default function DeviceSettings(props: Props) {
  return (
    <div className="DeviceSettings">
      <Legend>
        Notification preferences
        <p>Manage how you would like to receive Remind notifications.</p>
        <p>
          Any updates to your contact information below will only update in Remind. Let your school
          district know of any changes.
        </p>
      </Legend>
      <Query<IsRosteredEnforcement> query={IS_ROSTERED_ENFORCEMENT}>
        {({ loading: loadingRostered, data: dataRostered, error: errorRostered }) => {
          const canRosteredUsersLeaveOrg = dataRostered?.me?.organizations?.reduce(
            (response, org) => response && org.permissions.canRosteredUsersLeaveOrg,
            true
          );
          const isRosteredEnforcement = !canRosteredUsersLeaveOrg;

          return (
            <Query<Devices> query={DEVICES_QUERY}>
              {({ loading, data, error, refetch }) => {
                if (loading || loadingRostered) {
                  return <CenteredLoadingIndicator />;
                }

                if (error || !data || !dataRostered) {
                  console.log({ error });
                  console.log({ errorRostered });
                  return <ErrorReporter error="DeviceSettings - failed to load data" />;
                }

                const { devices, me } = data;
                const sortDevices = (devices: DeviceSettingsDevice[]) =>
                  sortBy(devices, ['label', 'id']);

                const pushDevices = devices
                  ? sortDevices(devices.filter(d => d.type === 'apns' || d.type === 'gcm'))
                  : [];
                const emailDevices = devices
                  ? sortDevices(devices.filter(d => d.type === 'email'))
                  : [];
                const smsDevices = devices
                  ? sortDevices(devices.filter(d => d.type === 'sms'))
                  : [];
                const landlineDevices = devices
                  ? sortDevices(devices.filter(d => d.type === 'landline'))
                  : [];

                const verifiedLoginDevices = devices ? devices.filter(d => d.verified) : [];
                const onlyOneLoginDevice = verifiedLoginDevices.length === 1;

                return (
                  <Container flush align="left" size="medium" className="DeviceSettings">
                    <Row size="small">
                      {emailDevices.map(emailDevice => (
                        <DeviceSettingsEmailItem
                          isRosteredEnforcement={isRosteredEnforcement}
                          emailDevice={emailDevice}
                          deviceIsOnlyLoginDevice={onlyOneLoginDevice}
                          refetch={refetch}
                        />
                      ))}
                      {props.showSmsDevices &&
                        smsDevices.map(SMSDevice => (
                          <DeviceSettingsSMSItem
                            isRosteredEnforcement={isRosteredEnforcement}
                            SMSDevice={SMSDevice}
                            deviceIsOnlyLoginDevice={onlyOneLoginDevice}
                            refetch={refetch}
                          />
                        ))}
                      {me &&
                        me.pendingSmsDevices.map(pendingSmsDevice => (
                          <DeviceSettingsPendingSMSItem
                            pendingSMSDevice={pendingSmsDevice}
                            refetch={refetch}
                          />
                        ))}
                      {landlineDevices.map(landlineDevice => (
                        <DeviceSettingsLandlineItem
                          isRosteredEnforcement={isRosteredEnforcement}
                          landlineDevice={landlineDevice}
                        />
                      ))}
                      {pushDevices.map(pushDevice => (
                        <DeviceSettingsPushItem
                          isRosteredEnforcement={isRosteredEnforcement}
                          pushDevice={pushDevice}
                        />
                      ))}
                      <DeviceSettingsDesktopItem />
                      {emailDevices.length === 0 && (
                        <DeviceSettingsEmptyItem refetchDevices={refetch} deviceType="email" />
                      )}
                      {props.showSmsDevices && smsDevices.length === 0 && (
                        <DeviceSettingsEmptyItem refetchDevices={refetch} deviceType="sms" />
                      )}
                      {pushDevices.length === 0 && (
                        <DeviceSettingsEmptyItem refetchDevices={refetch} deviceType="apns" />
                      )}
                    </Row>
                    <AddDeviceMenuButton onAddDevice={refetch} />
                  </Container>
                );
              }}
            </Query>
          );
        }}
      </Query>
    </div>
  );
}
